import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './BottomSheet.scss'

type Props = {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  title?: React.ReactNode
  className?: string
}

const c = 'bottom-sheet'
const CLOSE_DISTANCE = 110

const BottomSheet = ({ open, onClose, children, title, className }: Props) => {
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  const [drag, setDrag] = useState(0)
  const startY = useRef<number | null>(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      )
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const id = window.setTimeout(() => setMounted(false), 380)
    return () => window.clearTimeout(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    document.body.classList.add('body-no-scroll')
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.classList.remove('body-no-scroll')
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!mounted) return null

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY
  }
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null) return
    setDrag(Math.max(0, e.touches[0].clientY - startY.current))
  }
  const onTouchEnd = () => {
    if (drag > CLOSE_DISTANCE) onClose()
    startY.current = null
    setDrag(0)
  }

  return createPortal(
    <div className={`${c}${visible ? ' is-visible' : ''} ${className ?? ''}`}>
      <div
        className={`${c}__backdrop`}
        onClick={onClose}
        style={drag ? { opacity: Math.max(0, 1 - drag / 400) } : undefined}
      />
      <div
        className={`${c}__panel`}
        role='dialog'
        aria-modal='true'
        style={
          drag
            ? { transform: `translateY(${drag}px)`, transition: 'none' }
            : undefined
        }
      >
        <div
          className={`${c}__grab`}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <span />
          {title && <div className={`${c}__title`}>{title}</div>}
        </div>
        <div className={`${c}__content`}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}

export default BottomSheet
